import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Theme, alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import { DailyExpenseGapBranch } from "../../types/daily-expense.type";
import { numberWithCommas } from "../../utils";
import { EMPTY_CELL_TEXT } from "./constants";
import {
  GapCellKind,
  gapCellKindOf,
  isGapCellOpenable,
  sortGapBranches,
} from "./dailyExpenseReview";
import { formatBusinessDate, weekdayShortOf } from "./format";
import { BODY_ROW_SX, HEAD_ROW_SX, tableContainerSx } from "./tableStyles";

const TABLE_MAX_HEIGHT = 600;
const CELL_WIDTH = 30;
const CELL_HEIGHT = 26;
const SURFACE_OPACITY = 0.16;
const SUNDAY = 0;
const LEGEND_SWATCH_SIZE = 16;

interface GapCellStyle {
  mark: string;
  label: string;
  color: (theme: Theme) => string;
  background: (theme: Theme) => string;
  /** ยังไม่กรอก is the gap the view exists to show: a dashed red outline. */
  isDashed?: boolean;
}

const GAP_CELL: Record<GapCellKind, GapCellStyle> = {
  APPROVED: {
    mark: "✓",
    label: "อนุมัติ",
    color: (theme) => theme.palette.success.dark,
    background: (theme) => alpha(theme.palette.success.main, SURFACE_OPACITY),
  },
  SUBMITTED: {
    mark: "•",
    label: "รออนุมัติ",
    color: (theme) => theme.palette.warning.dark,
    background: (theme) => alpha(theme.palette.warning.main, SURFACE_OPACITY),
  },
  REJECTED: {
    mark: "!",
    label: "ตีกลับ",
    color: (theme) => theme.palette.error.dark,
    background: (theme) => alpha(theme.palette.error.main, SURFACE_OPACITY),
  },
  DRAFT: {
    mark: "ร",
    label: "ร่าง (ยังไม่ส่ง)",
    color: (theme) => theme.palette.gray.main,
    background: (theme) => alpha(theme.palette.gray.main, SURFACE_OPACITY),
  },
  NOT_STARTED: {
    mark: "",
    label: "ยังไม่กรอก",
    color: (theme) => theme.palette.error.main,
    background: (theme) => theme.palette.background.paper,
    isDashed: true,
  },
  OFF: {
    mark: EMPTY_CELL_TEXT,
    label: "ไม่ได้วิ่ง",
    color: (theme) => theme.palette.text.disabled,
    background: () => "transparent",
  },
};

const LEGEND_ORDER: GapCellKind[] = ["APPROVED", "SUBMITTED", "REJECTED", "DRAFT", "NOT_STARTED", "OFF"];

const cellSx = (style: GapCellStyle, isOpenable: boolean) => ({
  width: CELL_WIDTH,
  height: CELL_HEIGHT,
  borderRadius: 1.5,
  fontSize: "0.75rem",
  fontWeight: 600,
  color: style.color,
  bgcolor: style.background,
  border: 1,
  borderStyle: style.isDashed ? "dashed" : "solid",
  borderColor: style.isDashed ? "error.main" : "transparent",
  cursor: isOpenable ? "pointer" : "default",
});

interface ReviewGapTableProps {
  gap: DailyExpenseGapBranch[];
  dates: string[];
  onOpen: (branchCode: string, businessDate: string) => void;
}

/**
 * "สาขาที่ยังไม่ส่ง": every branch against every day of the range, so a missing day stands out
 * at a glance. The branches with the most days still to hand over come first; a day already
 * handed over opens its document.
 */
export default function ReviewGapTable({ gap, dates, onOpen }: Readonly<ReviewGapTableProps>) {
  const branches = sortGapBranches(gap);
  const openCell = (branchCode: string, businessDate: string) => () =>
    onOpen(branchCode, businessDate);

  return (
    <Paper>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={1.5}>
        <Typography fontWeight={600}>สถานะเอกสาร สาขา × วัน</Typography>
        <Stack direction="row" gap={1.75} flexWrap="wrap" sx={{ color: "text.secondary" }}>
          {LEGEND_ORDER.map((kind) => (
            <Stack key={kind} direction="row" alignItems="center" gap={0.75}>
              <Box
                sx={{
                  ...cellSx(GAP_CELL[kind], false),
                  width: LEGEND_SWATCH_SIZE,
                  height: LEGEND_SWATCH_SIZE,
                }}
              />
              <Typography variant="caption">{GAP_CELL[kind].label}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
      <TableContainer sx={tableContainerSx(TABLE_MAX_HEIGHT)}>
        <Table stickyHeader size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
          <TableHead>
            <TableRow sx={HEAD_ROW_SX}>
              <TableCell sx={{ position: "sticky", left: 0, zIndex: 3 }}>สาขา</TableCell>
              {dates.map((date) => (
                <TableCell key={date} align="center" sx={{ px: 0.25, lineHeight: 1.2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {dayjs(date).date()}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={dayjs(date).day() === SUNDAY ? "error.main" : "text.secondary"}
                  >
                    {weekdayShortOf(date)}
                  </Typography>
                </TableCell>
              ))}
              <TableCell align="right">ยังไม่ส่ง</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {branches.map((branch) => (
              <TableRow key={branch.branch_code} sx={BODY_ROW_SX}>
                <TableCell sx={{ position: "sticky", left: 0, zIndex: 1, bgcolor: "inherit" }}>
                  <Tooltip title={branch.branch_name}>
                    <span>{branch.branch_code}</span>
                  </Tooltip>
                </TableCell>
                {dates.map((date) => {
                  const kind = gapCellKindOf(branch, date);
                  const style = GAP_CELL[kind];
                  const isOpenable = isGapCellOpenable(kind);
                  return (
                    <TableCell key={date} align="center" sx={{ p: 0.375 }}>
                      <Tooltip title={`${branch.branch_code} · ${formatBusinessDate(date)} · ${style.label}`}>
                        {/* A disabled button fires no hover of its own; the span carries the tooltip. */}
                        <span>
                          <ButtonBase
                            disabled={!isOpenable}
                            onClick={openCell(branch.branch_code, date)}
                            sx={cellSx(style, isOpenable)}
                          >
                            {style.mark}
                          </ButtonBase>
                        </span>
                      </Tooltip>
                    </TableCell>
                  );
                })}
                <TableCell
                  align="right"
                  sx={{ fontWeight: 600, color: branch.missing_count > 0 ? "error.main" : "text.disabled" }}
                >
                  {branch.missing_count > 0
                    ? `${numberWithCommas(branch.missing_count)} วัน`
                    : EMPTY_CELL_TEXT}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
        คลิกช่องที่ส่งแล้วเพื่อเปิดเอกสาร · ช่อง {EMPTY_CELL_TEXT} = สาขาไม่ได้วิ่งวันนั้น
      </Typography>
    </Paper>
  );
}
